// src/pages/UsageStartup.jsx
import React, { Component, Fragment } from "react";
import { Helmet } from 'react-helmet';
import HeaderOne from '../components/header/HeaderOne';
import Footer from '../components/footer/Footer';
import ScrollToTop from 'react-scroll-up';
import { FiChevronUp, FiTerminal, FiGithub, FiDownload } from "react-icons/fi"; // Added some icons
import Breadcrumb from "../components/common/Breadcrumb";
import { Link } from 'react-router-dom'; // Import Link for internal navigation

class UsageStartup extends Component {
    render() {
        return (
            <Fragment>
                <Helmet>
                    <title>Getting Started with Eidocell | Eidocell Documentation</title>
                    <meta name="description" content="Learn how to set up your environment, install dependencies, and run Eidocell from source for your single-cell image analysis." />
                </Helmet>

                <HeaderOne />

                <Breadcrumb title={'Getting Started with Eidocell'} />

                <div className="main-content-area ptb--120 bg_color--1">
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-10 offset-lg-1">
                                <div className="startup-guide-content">
                                    <p className="lead">
                                        This guide will walk you through the essential steps to set up Eidocell by running it from the source code and introduce you to its basic workflow for single-cell image analysis.
                                    </p>
                                    <p className="text-white opacity-90 mb--30">
                                        <strong>Note:</strong> Direct executable builds for Windows/macOS are not currently available. Eidocell must be run from its Python source code.
                                    </p>

                                    <section id="installation" className="mb--40">
                                        <h2><FiTerminal className="heading-icon" /> 1. Installation & Setup from Source</h2>
                                        <p>To run Eidocell, you'll need Python installed on your system and then follow these steps to set up the project:</p>

                                        <h3>Setup Steps:</h3>
                                        <ol>
                                            <li>
                                                <strong>Clone the Repository:</strong>
                                                <p>Git is required to clone the repository. Download it from <a href="https://git-scm.com/downloads" target="_blank" rel="noopener noreferrer">git-scm.com</a>.
                                                Open your terminal or command prompt and navigate to the directory where you want to store Eidocell. Then, clone the repository:</p>
                                                <pre><code>git clone https://github.com/xuzzu/eidocell.git</code></pre>
                                                <p>Navigate into the cloned directory:</p>
                                                <pre><code>cd eidocell</code></pre>
                                            </li>
                                            <li>
                                                <strong>Create and Activate a Python Virtual Environment (Recommended):</strong>
                                                <p>It's recommended to create a virtual envrionment (you can use venv or Anaconda). Example: in the `eidocell` directory, run:</p>
                                                <pre><code>python -m venv eidocell-env</code></pre>
                                                <p>Activate the virtual environment:</p>
                                                <ul>
                                                    <li>On Windows: <pre><code>eidocell-env\Scripts\activate</code></pre></li>
                                                    <li>On macOS/Linux: <pre><code>source eidocell-env/bin/activate</code></pre></li>
                                                </ul>
                                                <p>Your terminal prompt should now indicate that you are in the `(eidocell-env)`.</p>
                                            </li>
                                            <li>
                                                <strong>Install Dependencies:</strong>
                                                <p>The `requirements.txt` file lists Python dependencies. Install them using pip:</p>
                                                <pre><code>pip install -r requirements.txt</code></pre>
                                                <p>This command will download and install all necessary libraries.</p>
                                            </li>
                                            <li>
                                                <strong>Run Eidocell:</strong>
                                                <p>Once all dependencies are installed, you can run the main application script. You might also need to add the <code>src</code> to the environment's PYTHONPATH.</p>
                                                <pre><code>python src/UI/main_window.py</code></pre>
                                            </li>
                                        </ol>
                                    </section>

                                    <section id="system-requirements" className="mb--40">
                                        <h2>2. System Requirements</h2>
                                        <p>To ensure smooth performance when running from source, consider these requirements:</p>
                                        <div className="responsive-table-wrapper">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Component</th>
                                                        <th>Minimum</th>
                                                        <th>Recommended</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>Processor</td>
                                                        <td>Dual-core 2.0 GHz</td>
                                                        <td>Quad-core 2.5 GHz or higher</td>
                                                    </tr>
                                                    <tr>
                                                        <td>RAM</td>
                                                        <td>8 GB</td>
                                                        <td>16 GB or more (especially for large datasets or AI models)</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Disk Space</td>
                                                        <td>~500MB for source & dependencies (can vary)</td>
                                                        <td>SSD with 10GB+ free space for data and results</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Graphics Card (Optional for AI)</td>
                                                        <td>-</td>
                                                        <td>NVIDIA GPU with CUDA support (for GPU-accelerated ONNX models, to be supported later)</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </section>

                                    <section id="first-launch" className="mb--40">
                                        <h2>3. First Launch & Interface Overview</h2>
                                        <p>Upon launching Eidocell (after running the Python script), you'll be greeted by the main interface. Key areas include:</p>
                                        <ul>
                                            <li><strong>Navigation Bar:</strong> General application and session control.</li>
                                            <li><strong>Top Navigation Panel:</strong> Switch between different views when working in a session.</li>
                                            <li><strong>Gallery View:</strong> Browse imported images, adjust scaling, apply filtering and sorting.</li>
                                            <li><strong>Clustering View:</strong> Select your preferred clustering and feature selection method, adjust clusters by splitting and merging.</li>
                                            <li><strong>Segmentation View:</strong> Tools and parameters for cell segmentation and complex masks.</li>
                                            <li><strong>Analysis View:</strong> Visualize data, generate plots, and perform interactive gating.</li>
                                        </ul>
                                    </section>

                                    <section id="basic-workflow" className="mb--40">
                                        <h2>4. Basic Workflow: A Quick Tour</h2>
                                        <p>Here’s a simplified walkthrough of a typical analysis pipeline in Eidocell:</p>
                                        <ol>
                                            <li>
                                                <strong>Import Images:</strong>
                                                <p>Go to <code>Session - New Session</code> or <code>Session - Open</code>. To import new images, create a new session and select a folder with your images or data.
                                                Add your custom pre-processing code if your data requires it.
                                                Common image formats are supported (like TIFF, PNG, JPG). Images will appear in the Gallery View.</p>
                                            </li>
                                            <li>
                                                <strong>Segmentation:</strong>
                                                <p>Navigate to the Segmentation View. Choose a segmentation method (e.g., Otsu, Watershed). Adjust parameters like threshold values, minimum cell size, etc. Preview the segmentation results in real-time. Once satisfied, apply the segmentation.</p>
                                            </li>
                                            <li>
                                                <strong>Feature Extraction:</strong>
                                                <p>With segmented cells basic quantitative features are available (e.g. area, circularity). If using AI-based feature extractors (like DINOv2), ensure the model weights are downloaded and put in the <code>src/backend/resources</code> folder. Run the extraction process.</p>
                                            </li>
                                            <li>
                                                <strong>Clustering & Classification (Optional):</strong>
                                                <p>Use the extracted features to group similar cells using clustering algorithms (e.g., K-means) or classify cells into predefined categories if you have training data.</p>
                                            </li>
                                            <li>
                                                <strong>Visualization & Export:</strong>
                                                <p>Explore your data in the Analysis View. Generate histograms and scatter plots to understand feature distributions and relationships.</p>
                                            </li>
                                        </ol>
                                    </section>

                                    <section id="next-steps" className="mb--40">
                                        <h2>5. Next Steps & Resources</h2>
                                        <p>This guide provides a basic introduction. For more complex stuff:</p>
                                        <ul>
                                            <li>Visit the <a href="https://github.com/xuzzu/eidocell" target="_blank" rel="noopener noreferrer">GitHub repository</a> to report issues, suggest features, or contribute to the project.</li>
                                            <li>Learn more <Link to="/about">About Eidocell</Link> and its development.</li>
                                        </ul>
                                    </section>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Footer />

                <div className="backto-top">
                    <ScrollToTop showUnder={160}>
                        <FiChevronUp />
                    </ScrollToTop>
                </div>
            </Fragment>
        )
    }
}

export default UsageStartup;